import { EDevice } from '../redux/sequence/enums';

/**
 * Single source of truth for responsive breakpoints and the layout grid.
 *
 * CSS media queries cannot read JS or CSS variables, so the same values are
 * mirrored in:
 *  - src/index.css             (@theme --breakpoint-md / --breakpoint-lg)
 *  - src/styles/layout-grid.css (grid tokens per tier)
 * `src/tests/unit/breakpoints.spec.ts` fails if they drift apart.
 *
 * Mobile-first: S is the base (no Tailwind prefix), `md:` = M, `lg:` = L.
 */

export interface Breakpoint {
  name: 'sm' | 'md' | 'lg';
  device: EDevice;
  /** Inclusive min-width in px. */
  minWidth: number;
  /** Outer margin in px. */
  margin: number;
  /** Column gutter in px. */
  gutter: number;
  columns: number;
}

/** Ordered smallest to largest. */
export const BREAKPOINTS: readonly Breakpoint[] = [
  {
    name: 'sm',
    device: EDevice.MOBILE,
    minWidth: 0,
    margin: 16,
    gutter: 16,
    columns: 4,
  },
  {
    name: 'md',
    device: EDevice.TABLET,
    minWidth: 640,
    margin: 32,
    gutter: 24,
    columns: 6,
  },
  {
    name: 'lg',
    device: EDevice.DESKTOP,
    minWidth: 899,
    margin: 32,
    gutter: 24,
    columns: 12,
  },
];

export const getDeviceForWidth = (width: number): EDevice =>
  [...BREAKPOINTS].reverse().find((bp) => width >= bp.minWidth)!.device;
